import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { IoChevronBack } from "react-icons/io5";
import { FiLock } from "react-icons/fi";
import {
  getScopeSchedule,
  previewScopeSchedule,
  applyScopeSchedule,
} from "../../../../../services/scheduleEditorService";
import { StatusPill, CountPill } from "./primitives";
import ActivitiesStep from "./steps/ActivitiesStep";
import MappingStep from "./steps/MappingStep";
import InspectionPlanStep from "./steps/InspectionPlanStep";
import BillingMilestonesStep from "./steps/BillingMilestonesStep";
import CustomerPaymentsStep from "./steps/CustomerPaymentsStep";
import ReviewStep from "./steps/ReviewStep";
import headerBanner from "../../../../../assets/svg/ScheduleEditorHeader.svg";

const STEPS = [
  { id: "activities", title: "Activities" },
  { id: "mapping", title: "Map to Billing Milestones" },
  { id: "inspection", title: "Inspection Plan" },
  { id: "billing", title: "Billing & Payouts" },
  { id: "payments", title: "Customer Payments" },
  { id: "review", title: "Review & Save" },
];

// Pill-style numbered step tab — ported from the portal's NavTabsLinkV2:
// active = black pill with white text, inactive = light grey pill.
const StepTab = ({ active, prefix, reachable, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      minHeight: "36px",
      height: "36px",
      // Not `disabled`: that greys the tab out and kills the cursor. The
      // look stays identical and goToStep already refuses the jump — only
      // the cursor says it is not available yet.
      cursor: reachable ? "pointer" : "not-allowed",
      borderRadius: "24px",
      padding: "0 12px",
      fontSize: "12px",
      fontWeight: 500,
      border: "none",
      whiteSpace: "nowrap",
      backgroundColor: active ? "#000000" : "#F7F7F7",
      color: active ? "#FFFFFF" : "#9CA3AF",
      fontFamily: "Inter Tight",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      flexShrink: 0,
    }}
  >
    {!!prefix && (
      <span
        style={{
          fontSize: "12px",
          padding: "4px 8px",
          color: "#949DAA",
          borderRadius: "24px",
          backgroundColor: active ? "#FFFFFF33" : "#FFFFFF",
        }}
      >
        {prefix}
      </span>
    )}
    {children}
  </button>
);

const ScheduleEditor = () => {
  const { id: projectId, scopeId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);

  // Drafts hold only what the user touched, keyed by id, so a background
  // refetch never overwrites an edit in progress.
  const [activityDrafts, setActivityDrafts] = useState({});
  const [groupDrafts, setGroupDrafts] = useState({});
  const [paymentDrafts, setPaymentDrafts] = useState({});
  // Still sent with every save so the audit log has a category, but no
  // longer asked for on screen.
  const reasonCategory = "SCHEDULE_UPDATE";
  const notes = "";
  const [preview, setPreview] = useState(null);

  const fetchSchedule = useCallback(async () => {
    try {
      const response = await getScopeSchedule(scopeId);
      setSchedule(response?.data || null);
      setLoadError(null);
    } catch (err) {
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  }, [scopeId]);

  useEffect(() => {
    setLoading(true);
    fetchSchedule();
  }, [fetchSchedule]);

  const scopeName =
    schedule?.scope?.name || searchParams.get("scopeName") || "Scope";
  const editable = !!schedule?.editable;

  const activityValue = useCallback(
    (activity, field) => {
      const draft = activityDrafts[activity.id];
      if (draft && draft[field] !== undefined) return draft[field];
      return activity[field] ?? null;
    },
    [activityDrafts],
  );

  const groupValue = useCallback(
    (group) => {
      const draft = groupDrafts[group.group_id];
      return draft !== undefined ? draft : group.due_date ?? null;
    },
    [groupDrafts],
  );

  const paymentValue = useCallback(
    (line) => {
      const draft = paymentDrafts[line.id];
      return draft !== undefined ? draft : line.due_date ?? null;
    },
    [paymentDrafts],
  );

  // Only changed rows travel to the server — the backend treats the payload
  // as a diff and ignores anything that already matches.
  const edits = useMemo(() => {
    if (!schedule) return null;

    return {
      activities: (schedule.activities || [])
        .filter((a) => activityDrafts[a.id])
        .map((a) => ({
          id: a.id,
          start_date: activityValue(a, "start_date"),
          end_date: activityValue(a, "end_date"),
        })),
      billing_milestones: (schedule.billing_milestones || [])
        .filter((g) => groupDrafts[g.group_id] !== undefined)
        .map((g) => ({ group_id: g.group_id, due_date: groupValue(g) })),
      customer_payments: (schedule.customer_payments || [])
        .filter((l) => paymentDrafts[l.id] !== undefined)
        .map((l) => ({ id: l.id, due_date: paymentValue(l) })),
    };
  }, [
    schedule,
    activityDrafts,
    groupDrafts,
    paymentDrafts,
    activityValue,
    groupValue,
    paymentValue,
  ]);

  const hasChanges =
    !!edits &&
    (edits.activities.length > 0 ||
      edits.billing_milestones.length > 0 ||
      edits.customer_payments.length > 0);

  // Preview runs continuously, not just on the last step, so the stepper can
  // flag the step that owns each blocker. Debounced because clearing and
  // re-picking a date fires twice in quick succession.
  const previewSeq = useRef(0);
  useEffect(() => {
    if (!schedule || !editable) return undefined;

    const timer = setTimeout(async () => {
      const seq = ++previewSeq.current;
      setPreviewing(true);
      try {
        const response = await previewScopeSchedule(scopeId, {
          ...edits,
          reason_category: reasonCategory,
        });
        // Ignore responses that arrive after a newer request fired.
        if (seq === previewSeq.current) setPreview(response?.data || null);
      } catch (err) {
        if (seq === previewSeq.current && err?.blockers) {
          setPreview({ blockers: err.blockers, plan: {} });
        }
      } finally {
        if (seq === previewSeq.current) setPreviewing(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [scopeId, schedule, editable, edits, reasonCategory]);

  // The Schedule tab, not Milestones — the editor is launched from Schedule.
  const handleBack = useCallback(
    () => navigate(`/project/details/${projectId}/schedule`),
    [navigate, projectId],
  );

  const goToStep = (index) => {
    if (index <= currentStep || completedSteps.includes(STEPS[index].id)) {
      setCurrentStep(index);
    }
  };

  const handleNext = () => {
    if (currentStep >= STEPS.length - 1) return;
    setCompletedSteps((prev) => [...new Set([...prev, STEPS[currentStep].id])]);
    setCurrentStep((step) => step + 1);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await applyScopeSchedule(scopeId, {
        ...edits,
        reason_category: reasonCategory,
        notes,
      });
      const data = response?.data;
      const w = data?.written || {};
      toast.success(
        `Schedule saved — ${w.activities || 0} activities, ` +
          `${w.groups || 0} billing milestones, ${w.payouts || 0} payouts updated.`,
      );

      // Customer payment dates go through the middleware after the
      // transaction, so they can fail on their own. Everything else is
      // already saved at this point — say so rather than let it pass as a
      // clean success.
      const payments = data?.payment_lines_result;
      if (payments && payments.ok === false) {
        const count = payments.failed?.length || 0;
        toast.warning(
          `The schedule saved, but ${count} customer payment date` +
            `${count === 1 ? "" : "s"} could not be updated: ` +
            `${payments.failed?.[0]?.error || "the payment service did not respond"}.`,
        );
      }
      setActivityDrafts({});
      setGroupDrafts({});
      setPaymentDrafts({});
      setPreview(null);
      handleBack();
    } catch (err) {
      if (err?.blockers?.length) {
        setPreview({ blockers: err.blockers, plan: {} });
        toast.error("Fix the blockers before saving.");
      } else {
        toast.error(err?.message || "Could not save the schedule.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#F9FAFB",
          fontFamily: "Inter Tight",
        }}
      >
        <p style={{ color: "#6B7280" }}>Loading schedule…</p>
      </div>
    );
  }

  if (loadError || !schedule) {
    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          backgroundColor: "#F9FAFB",
          fontFamily: "Inter Tight",
        }}
      >
        <p style={{ color: "#374151" }}>
          {loadError?.message || "Schedule could not be loaded."}
        </p>
        <button
          type="button"
          onClick={handleBack}
          style={{
            padding: "8px 16px",
            border: "1px solid #D1D5DB",
            borderRadius: "8px",
            backgroundColor: "#fff",
            color: "#374151",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            fontFamily: "Inter Tight",
          }}
        >
          Back to Schedule
        </button>
      </div>
    );
  }

  const stepId = STEPS[currentStep].id;
  const canSave = hasChanges && preview?.can_apply && !saving;

  const stepProps = {
    schedule,
    scopeId,
    editable,
    // Derived dates are only known after the backend has run the derivation,
    // so the steps that display them read the projected values off the
    // preview.
    preview,
    activityDrafts,
    setActivityDrafts,
    activityValue,
    groupDrafts,
    setGroupDrafts,
    groupValue,
    paymentDrafts,
    setPaymentDrafts,
    paymentValue,
    // Mapping writes immediately and rewrites the schedule optimistically —
    // these replace the portal's react-query cache mutation/invalidation.
    setSchedule,
    refetchSchedule: fetchSchedule,
  };

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100vh",
        padding: "24px",
        gap: "16px",
        boxSizing: "border-box",
        fontFamily: "Inter Tight",
      }}
    >
      {/* base project header banner */}
      <div
        style={{
          backgroundImage: `url(${headerBanner})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: "224px",
          position: "absolute",
          top: 0,
          right: 0,
          width: "100%",
          zIndex: -1,
        }}
      />

      {/* -------- header -------- */}
      <div style={{ width: "100%", flexGrow: 0 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: "32px",
            }}
          >
            <button
              type="button"
              onClick={handleBack}
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                border: "1px solid #E5E7EB",
                backgroundColor: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <IoChevronBack />
            </button>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <span style={{ color: "#0d0d0e" }}>Schedule — {scopeName}</span>
              <StatusPill
                status={
                  schedule.bid_project?.status || schedule.project?.status
                }
              />
              <CountPill>
                {schedule.activities?.length || 0} activities
              </CountPill>
              {hasChanges && <CountPill tone="amber">Unsaved changes</CountPill>}
            </div>
          </div>

          <button
            type="button"
            onClick={handleBack}
            style={{
              padding: "6px 14px",
              border: "1px solid #D1D5DB",
              borderRadius: "8px",
              backgroundColor: "transparent",
              color: "#374151",
              fontSize: "13px",
              fontWeight: 500,
              cursor: "pointer",
              fontFamily: "Inter Tight",
            }}
          >
            Cancel
          </button>
        </div>

        {/* -------- numbered step tabs -------- */}
        <div
          style={{
            marginTop: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            overflowX: "auto",
            paddingBottom: "4px",
          }}
        >
          {STEPS.map((step, index) => {
            const reachable =
              index <= currentStep || completedSteps.includes(step.id);
            return (
              <StepTab
                key={step.id}
                active={index === currentStep}
                prefix={String(index + 1)}
                reachable={reachable}
                onClick={() => goToStep(index)}
              >
                {step.title}
              </StepTab>
            );
          })}
        </div>
      </div>

      {/* -------- content card -------- */}
      <div
        style={{
          flexGrow: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#fff",
          borderRadius: "12px",
          border: "1px solid #F3F4F6",
          overflow: "hidden",
        }}
      >
        <div style={{ flex: 1, overflow: "auto", padding: "24px" }}>
          {!editable && (
            <div
              style={{
                marginBottom: "16px",
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                border: "1px solid #FDE68A",
                backgroundColor: "#FFFBEB",
                borderRadius: "12px",
                padding: "12px 16px",
              }}
            >
              <FiLock
                style={{
                  color: "#D97706",
                  marginTop: "2px",
                  flexShrink: 0,
                }}
              />
              <div style={{ fontSize: "14px", color: "#78350F" }}>
                <span style={{ fontWeight: 500 }}>
                  This schedule is read-only.
                </span>{" "}
                It can only be edited while the scope is in{" "}
                <code style={{ fontSize: "12px" }}>ready_to_start</code>. Use a
                Change Request to move dates after that.
              </div>
            </div>
          )}

          {stepId === "activities" && <ActivitiesStep {...stepProps} />}
          {stepId === "mapping" && <MappingStep {...stepProps} />}
          {stepId === "inspection" && <InspectionPlanStep {...stepProps} />}
          {stepId === "billing" && <BillingMilestonesStep {...stepProps} />}
          {stepId === "payments" && <CustomerPaymentsStep {...stepProps} />}
          {stepId === "review" && (
            <ReviewStep
              schedule={schedule}
              preview={preview}
              previewing={previewing}
            />
          )}
        </div>

        {/* -------- footer — pinned at the bottom -------- */}
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
            borderTop: "1px solid #F3F4F6",
            backgroundColor: "#fff",
          }}
        >
          <button
            type="button"
            onClick={() => setCurrentStep((step) => Math.max(0, step - 1))}
            disabled={currentStep === 0}
            style={{
              padding: "8px 16px",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              color: "#374151",
              fontWeight: 500,
              fontSize: "14px",
              backgroundColor: "#fff",
              cursor: currentStep === 0 ? "not-allowed" : "pointer",
              opacity: currentStep === 0 ? 0.5 : 1,
              fontFamily: "Inter Tight",
            }}
          >
            Previous
          </button>

          {stepId === "review" ? (
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              title={
                !hasChanges
                  ? "Nothing changed yet"
                  : !preview?.can_apply
                  ? "Blockers must be fixed first"
                  : undefined
              }
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                color: "#fff",
                fontWeight: 500,
                fontSize: "14px",
                border: "none",
                backgroundColor: "#4D6BF6",
                cursor: !canSave ? "not-allowed" : "pointer",
                opacity: !canSave ? 0.5 : 1,
                fontFamily: "Inter Tight",
              }}
            >
              {saving ? "Saving…" : "Save Schedule"}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNext}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                color: "#fff",
                fontWeight: 500,
                fontSize: "14px",
                border: "none",
                backgroundColor: "#4D6BF6",
                cursor: "pointer",
                fontFamily: "Inter Tight",
              }}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleEditor;
